import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  User, Mail, Lock, ArrowRight, Shield,
  HeartPulse, CheckCircle2, ChevronLeft, CheckCircle,
} from 'lucide-react';
import { TextInput, Alert } from '../components/UI';
import { API_URL, API_KEY } from '../config';

const CHECKS = [
  { test: v => v.length >= 8,    label: 'At least 8 characters' },
  { test: v => /[A-Z]/.test(v), label: 'One uppercase letter' },
  { test: v => /[0-9]/.test(v), label: 'One number' },
];

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .su-root {
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'DM Sans', system-ui, sans-serif;
    background: #f0f4f8;
    padding: 40px 20px;
    position: relative;
    overflow: hidden;
  }

  .su-root::before {
    content: '';
    position: fixed; inset: 0;
    background-image: radial-gradient(circle, rgba(0,80,140,0.055) 1px, transparent 1px);
    background-size: 28px 28px;
    pointer-events: none;
  }

  .su-blob1 {
    position: fixed; top: -160px; left: -160px;
    width: 480px; height: 480px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,130,200,0.09) 0%, transparent 70%);
    pointer-events: none;
  }
  .su-blob2 {
    position: fixed; bottom: -120px; right: -120px;
    width: 400px; height: 400px; border-radius: 50%;
    background: radial-gradient(circle, rgba(0,160,200,0.07) 0%, transparent 70%);
    pointer-events: none;
  }

  .su-card {
    position: relative; z-index: 1;
    width: 100%; max-width: 520px;
    background: #ffffff;
    border-radius: 20px;
    padding: 44px 48px 40px;
    box-shadow: 0 8px 40px rgba(0,60,120,0.10), 0 1px 4px rgba(0,60,120,0.06);
    border: 1px solid rgba(0,100,160,0.08);
  }

  .su-logo {
    display: block;
    width: 220px; height: 44px;
    object-fit: contain;
    margin: 0 auto 16px;
  }
  .su-logo-rule {
    width: 40px; height: 2px;
    background: linear-gradient(90deg, rgba(0,130,200,0.5), transparent);
    border-radius: 2px;
    margin: 0 auto 24px;
  }

  .su-chip {
    display: inline-flex; align-items: center;
    padding: 4px 12px;
    background: rgba(0,110,170,0.08);
    border: 1px solid rgba(0,110,170,0.16);
    border-radius: 100px;
    font-size: 10px; font-weight: 600;
    letter-spacing: 1.6px; text-transform: uppercase;
    color: #006090; margin-bottom: 12px;
  }

  .su-title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 30px; font-weight: 700;
    color: #0d2a42; letter-spacing: -0.3px;
    line-height: 1.13; margin-bottom: 6px;
  }

  .su-sub {
    font-size: 13.5px; color: #7a96b0;
    line-height: 1.65; font-weight: 300; margin-bottom: 24px;
  }

  .su-grid2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
  }

  .su-fields { display: flex; flex-direction: column; gap: 14px; }

  .su-bars { display: flex; gap: 4px; margin-top: 8px; margin-bottom: 6px; }
  .su-bar  { flex: 1; height: 3px; border-radius: 2px; transition: background 0.3s; }
  .su-pw-checks { display: flex; flex-wrap: wrap; gap: 4px 14px; }
  .su-pw-check  { font-size: 11.5px; display: flex; align-items: center; gap: 4px; transition: color 0.2s; }

  .su-cta {
    width: 100%; height: 50px; border-radius: 12px;
    background: linear-gradient(128deg, #005a8e 0%, #0090c0 100%);
    border: none; color: #fff;
    font-size: 15px; font-weight: 600;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 4px 18px rgba(0,90,142,0.22);
    transition: transform 0.18s ease, box-shadow 0.18s ease;
    position: relative; overflow: hidden;
    margin-top: 22px;
  }
  .su-cta::before {
    content: '';
    position: absolute; top: 0; left: 0; right: 0; height: 50%;
    background: linear-gradient(180deg, rgba(255,255,255,0.12) 0%, transparent 100%);
    pointer-events: none;
  }
  .su-cta:hover:not(:disabled) { transform: translateY(-1px); box-shadow: 0 8px 26px rgba(0,90,142,0.30); }
  .su-cta:active:not(:disabled) { transform: translateY(0); }
  .su-cta:disabled { opacity: 0.6; cursor: not-allowed; }

  .su-or { display: flex; align-items: center; gap: 14px; margin: 20px 0; }
  .su-or-line { flex: 1; height: 1px; background: #dde6ef; }
  .su-or-txt  { font-size: 12px; color: #9ab0c4; white-space: nowrap; }

  .su-ghost {
    width: 100%; height: 48px; border-radius: 12px;
    border: 1.5px solid #c4d2e0; background: #f7fafd;
    color: #0d2a42; font-size: 14px; font-weight: 500;
    font-family: 'DM Sans', system-ui, sans-serif;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    transition: background 0.18s, border-color 0.18s;
  }
  .su-ghost:hover { background: #e6f0f8; border-color: rgba(0,130,190,0.3); }

  .su-trust {
    display: flex; align-items: center; justify-content: center;
    gap: 18px; margin-top: 20px; padding-top: 16px;
    border-top: 1px solid #dde6ef;
  }
  .su-badge { display: flex; align-items: center; gap: 5px; font-size: 10.5px; color: #96aec4; }

  .su-footer-note {
    text-align: center; margin-top: 14px;
    font-size: 11px; color: #a0b4c8; line-height: 1.7;
  }

  @keyframes su-up { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
  .a-u0 { animation: su-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.04s both; }
  .a-u1 { animation: su-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.12s both; }
  .a-u2 { animation: su-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.20s both; }
  .a-u3 { animation: su-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.28s both; }
  .a-u4 { animation: su-up 0.6s cubic-bezier(0.16,1,0.3,1) 0.36s both; }

  @media (max-width: 580px) {
    .su-card  { padding: 32px 22px 28px; }
    .su-grid2 { grid-template-columns: 1fr; }
    .su-title { font-size: 26px; }
    .su-logo  { width: 180px; height: 36px; }
  }
`;

export default function SignupPage() {
  const navigate = useNavigate();
  const [form, setForm]         = useState({ name: '', username: '', email: '', password: '', confirm: '' });
  const [errors, setErrors]     = useState({});
  const [apiError, setApiError] = useState('');
  const [loading, setLoading]   = useState(false);

  const set = (k, v) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })); };

  const validate = () => {
    const e = {};
    if (!form.name.trim())     e.name     = 'Full name is required.';
    if (!form.username.trim()) e.username  = 'Username is required.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address.';
    if (form.password.length < 8) e.password = 'Password must be at least 8 characters.';
    if (form.password !== form.confirm) e.confirm = 'Passwords do not match.';
    return e;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoading(true); setApiError('');
    try {
      const res = await fetch(`${API_URL}/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify({ name: form.name, username: form.username, email: form.email, password: form.password }),
      });
      const data = await res.json();
      if (res.ok) navigate('/login', { state: { message: 'Account created successfully. Please sign in.' } });
      else setApiError(data.message || 'Could not create account.');
    } catch { setApiError('Cannot connect to server.'); }
    finally { setLoading(false); }
  };

  const pwChecks   = CHECKS.map(c => ({ ...c, pass: form.password ? c.test(form.password) : null }));
  const pwStrength = pwChecks.filter(c => c.pass).length;
  const barColor   = n => pwStrength >= n ? (n === 1 ? '#dc2626' : n === 2 ? '#d97706' : '#059669') : '#dde6ef';
  const titleCase  = v => v.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

  return (
    <>
      <style>{CSS}</style>
      <div className="su-root">
        <div className="su-blob1" />
        <div className="su-blob2" />

        <div className="su-card">

          <div className="a-u0">
            <img src="/logo.png" alt="Cytometrix" className="su-logo" />
            <div className="su-logo-rule" />
          </div>

          <div className="a-u0">
            <div className="su-chip">New Account</div>
            <h2 className="su-title">Create your account</h2>
            <p className="su-sub">Join Cytometrix to access AI-powered urinalysis tools.</p>
          </div>

          {apiError && (
            <div style={{ marginBottom: 16 }} className="a-u1">
              <Alert type="error">{apiError}</Alert>
            </div>
          )}

          <form onSubmit={handleSignup}>
            <div className="su-fields a-u1">
              <TextInput
                label="Full Name" icon={User} required
                value={form.name} onChange={e => set('name', titleCase(e.target.value))}
                placeholder="Dr. Jane Smith" error={errors.name}
              />
            </div>

            <div className="su-grid2 a-u2" style={{ marginTop: 14 }}>
              <TextInput
                label="Username" icon={User} required
                value={form.username} onChange={e => set('username', e.target.value)}
                placeholder="janesmith" autoCapitalize="none" error={errors.username}
              />
              <TextInput
                label="Email Address" icon={Mail} type="email" required
                value={form.email} onChange={e => set('email', e.target.value)}
                placeholder="jane@hospital.ca" error={errors.email}
              />
            </div>

            <div className="su-fields a-u3" style={{ marginTop: 14 }}>
              <div>
                <TextInput
                  label="Password" icon={Lock} type="password" required
                  value={form.password} onChange={e => set('password', e.target.value)}
                  placeholder="Create a strong password" error={errors.password}
                />
                {form.password && (
                  <>
                    <div className="su-bars">
                      {[1,2,3].map(n => <div key={n} className="su-bar" style={{ background: barColor(n) }} />)}
                    </div>
                    <div className="su-pw-checks">
                      {pwChecks.map(c => (
                        <span key={c.label} className="su-pw-check" style={{
                          color: c.pass === null ? '#94a3b8' : c.pass ? '#059669' : '#dc2626',
                        }}>
                          <CheckCircle size={11} strokeWidth={2.5} /> {c.label}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </div>

              <TextInput
                label="Confirm Password" icon={Lock} type="password" required
                value={form.confirm} onChange={e => set('confirm', e.target.value)}
                placeholder="Repeat your password" error={errors.confirm}
              />
            </div>

            <button type="submit" className="su-cta a-u3" disabled={loading}>
              {loading ? 'Creating account…' : <><span>Create Account</span><ArrowRight size={16} strokeWidth={2.5} /></>}
            </button>
          </form>

          <div className="su-or a-u4">
            <div className="su-or-line" />
            <span className="su-or-txt">Already have an account?</span>
            <div className="su-or-line" />
          </div>

          <div className="a-u4">
            <button className="su-ghost" onClick={() => navigate('/login')}>
              <ChevronLeft size={15} strokeWidth={2.5} /> Sign In
            </button>
          </div>

          <div className="su-trust a-u4">
            {[['Encrypted', Shield], ['HIPAA Aligned', CheckCircle2], ['Wellness Only', HeartPulse]].map(([lbl, Icon]) => (
              <div key={lbl} className="su-badge">
                <Icon size={12} color="#96aec4" /><span>{lbl}</span>
              </div>
            ))}
          </div>

          <p className="su-footer-note">
            For authorized wellness professionals only.<br />
            Unauthorized access is strictly prohibited.
          </p>

        </div>
      </div>
    </>
  );
}